import * as React from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { LucideIcon, Settings, LogOut, User } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface UserProfileProps {
  user: {
    name: string
    email: string
    role: string
    avatar?: string
    initials: string
  }
  status?: {
    text: string
    color: 'green' | 'orange' | 'red'
  }
  actions?: Array<{
    label: string
    icon: LucideIcon
    onClick?: () => void
    variant?: 'default' | 'outline' | 'ghost'
  }>
  compact?: boolean
  className?: string
}

const statusColors = {
  green: 'bg-[#10b981]',
  orange: 'bg-[#f59e0b]', 
  red: 'bg-red-500'
}

export const UserProfile = React.forwardRef<HTMLDivElement, UserProfileProps>(
  ({ user, status, actions, compact = false, className, ...props }, ref) => {
    const defaultActions = [
      { label: 'Profile', icon: User, variant: 'outline' as const },
      { label: 'Settings', icon: Settings, variant: 'outline' as const },
      { label: 'Sign Out', icon: LogOut, variant: 'ghost' as const }
    ]

    const profileActions = actions || defaultActions

    if (compact) {
      return (
        <div ref={ref} className={cn('flex items-center space-x-3 p-2', className)} {...props}>
          <Avatar className="h-8 w-8">
            {user.avatar && <AvatarImage src={user.avatar} alt={user.name} />}
            <AvatarFallback>{user.initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.role}</p>
          </div>
          {status && (
            <div className={cn('h-2 w-2 rounded-full', statusColors[status.color])} />
          )}
        </div>
      )
    }

    return (
      <Card ref={ref} className={cn('w-full max-w-sm', className)} {...props}>
        <CardHeader className="text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <Avatar className="h-20 w-20">
                {user.avatar && <AvatarImage src={user.avatar} alt={user.name} />}
                <AvatarFallback className="text-lg">{user.initials}</AvatarFallback>
              </Avatar>
              {status && (
                <div className="absolute -bottom-1 -right-1 flex items-center space-x-1">
                  <div className={cn('h-3 w-3 rounded-full border-2 border-background', statusColors[status.color])} />
                </div>
              )}
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold">{user.name}</h3>
              <p className="text-sm text-muted-foreground">{user.email}</p>
              <Badge variant="secondary" className="text-xs">
                {user.role}
              </Badge>
              {status && (
                <p className="text-xs text-muted-foreground">
                  Status: {status.text}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-2">
          <Separator />
          <div className="flex flex-col space-y-2">
            {profileActions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'outline'}
                size="sm"
                className="w-full justify-start"
                onClick={'onClick' in action ? action.onClick : undefined}
              >
                <action.icon className="h-4 w-4 mr-2" />
                {action.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }
)

UserProfile.displayName = 'UserProfile'
